import React, { useCallback, useState } from 'react';
import { Collapse } from 'reactstrap';

import AudioBadge from '../StudyList/Badges/AudioBadge';
import PlaylistActionsMenu from './PlaylistActionsMenu';

const PERSONALIZED_STUDY_PLAYLIST_BASE =
  'Personalized Study Automatic Playlist';

const EMPTY_MESSAGES = {
  noPlaylists: "You haven't created any custom playlists yet.",
  both: 'No custom playlists have both Q&A and Summary audio content.',
  qa: 'No custom playlists have Q&A only audio content.',
  summary: 'No custom playlists have Summary only audio content.',
};

function CustomPlaylistSection({
  playlists,
  audioChapters,
  audioTypeFilter,
  audioPlaylistsProgress,
  onPlay,
  onEdit,
  onDelete,
  onRestart,
  highlightPSPlaylist,
}) {
  const [openPlaylistIds, setOpenPlaylistIds] = useState({});

  const psPlaylistRef = useCallback(
    (node) => {
      if (node && highlightPSPlaylist) {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    [highlightPSPlaylist],
  );

  const togglePlaylist = (playlistId) => {
    setOpenPlaylistIds((prev) => ({
      ...prev,
      [playlistId]: !prev[playlistId],
    }));
  };

  const enrichChapters = (playlist) => {
    if (!playlist?.chapters || !audioChapters?.length) return [];

    return [...playlist.chapters]
      .sort((a, b) => a.orderIndex - b.orderIndex)
      .map((pc) => {
        const found = audioChapters.find(
          (ac) => Number(ac.id) === Number(pc.chapterId),
        );
        if (!found) return null;
        return { ...found, orderIndex: pc.orderIndex };
      })
      .filter(Boolean)
      .filter((ch) => {
        if (audioTypeFilter === 'qa') return ch.hasPlaylist;
        if (audioTypeFilter === 'summary') return ch.hasAudioSummary;
        if (audioTypeFilter === 'both')
          return ch.hasPlaylist && ch.hasAudioSummary;
        return false; // fallback, should never reach here
      });
  };

  const getChapterProgress = (chapterId) => {
    const progressData = audioPlaylistsProgress?.[chapterId];
    if (!progressData || progressData.total <= 0) return 0;
    return Math.round((progressData.accumulated / progressData.total) * 100);
  };

  const hasNoPlaylists = !playlists || playlists.length === 0;

  const playlistsWithChapters = hasNoPlaylists
    ? []
    : playlists
        .map((playlist) => ({
          playlist,
          enrichedChapters: enrichChapters(playlist),
        }))
        .filter(({ enrichedChapters }) => enrichedChapters.length > 0);

  const showEmptyMessage = hasNoPlaylists || playlistsWithChapters.length === 0;
  const emptyMessage = hasNoPlaylists
    ? EMPTY_MESSAGES.noPlaylists
    : EMPTY_MESSAGES[audioTypeFilter] || EMPTY_MESSAGES.noPlaylists;

  const getPlaylistProgress = (enrichedChapters) => {
    if (!enrichedChapters.length)
      return { completed: 0, total: 0, percentage: 0 };

    const totalPercent = enrichedChapters.reduce((sum, ch) => {
      const progressData = audioPlaylistsProgress?.[ch.id];
      if (!progressData || progressData.total <= 0) return sum;
      return (
        sum + Math.round((progressData.accumulated / progressData.total) * 100)
      );
    }, 0);

    const percentage = Math.round(totalPercent / enrichedChapters.length);

    const completed = enrichedChapters.filter((ch) => {
      const progress = audioPlaylistsProgress?.[ch.id];
      return progress?.status === 'COMPLETED';
    }).length;

    return { completed, total: enrichedChapters.length, percentage };
  };

  const getResumeChapter = (enrichedChapters) => {
    // Find first non-completed chapter
    const firstIncomplete = enrichedChapters.find((ch) => {
      const progress = audioPlaylistsProgress?.[ch.id];
      return progress?.status !== 'COMPLETED';
    });

    // If all completed, start from the first
    return firstIncomplete || enrichedChapters[0];
  };

  const hasAnyProgress = (enrichedChapters) =>
    enrichedChapters.some((ch) => {
      const progress = audioPlaylistsProgress?.[ch.id];
      return progress?.accumulated > 0;
    });

  return (
    <div className="custom-playlist-section">
      {showEmptyMessage ? (
        <div className="audio-study-empty">{emptyMessage}</div>
      ) : (
        <div className="audio-study-list">
          {playlistsWithChapters.map(({ playlist, enrichedChapters }) => {
            const isOpen = !!openPlaylistIds[playlist.id];
            const progress = getPlaylistProgress(enrichedChapters);
            const hasStarted = hasAnyProgress(enrichedChapters);
            const isPSPlaylist = playlist.name.startsWith(
              PERSONALIZED_STUDY_PLAYLIST_BASE,
            );

            return (
              <div
                key={playlist.id}
                ref={isPSPlaylist ? psPlaylistRef : null}
                className={`audio-study-ref${isPSPlaylist && highlightPSPlaylist ? ' audio-study-ref--highlighted' : ''}`}
              >
                <button
                  type="button"
                  className="audio-study-ref__header"
                  onClick={() => togglePlaylist(playlist.id)}
                >
                  <div className="audio-study-ref__left">
                    <i
                      className={`icon-expand rotate ${isOpen ? 'down' : ''}`}
                    />
                    <div className="audio-study-ref__title">
                      {playlist.name}
                    </div>
                  </div>

                  <div className="audio-study-ref__meta">
                    <div className="audio-study-ref__info">
                      <span className="audio-study-ref__chapters">
                        {enrichedChapters.length} Chapters
                      </span>

                      <span className="custom-playlist-section__progress">
                        {progress.percentage}%
                      </span>
                    </div>

                    <span
                      className="custom-playlist-section__actions"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        className={`custom-playlist-section__play-btn ${
                          hasStarted
                            ? 'custom-playlist-section__play-btn--continue'
                            : ''
                        }`}
                        aria-label={
                          hasStarted
                            ? 'Continue studying playlist'
                            : 'Play playlist'
                        }
                        onClick={() => {
                          const target = getResumeChapter(enrichedChapters);
                          if (target) onPlay(enrichedChapters, target);
                        }}
                      >
                        <i className="material-icons">
                          {hasStarted
                            ? 'play_circle_filled'
                            : 'play_circle_outline'}
                        </i>
                      </button>

                      {/* 3-dot menu */}
                      <PlaylistActionsMenu
                        onEdit={() => onEdit(playlist)}
                        onDelete={() => onDelete(playlist)}
                      />
                    </span>
                  </div>
                </button>

                <Collapse isOpen={isOpen}>
                  <div className="audio-study-ref__body">
                    {enrichedChapters.map((ch) => (
                      <div
                        key={ch.id}
                        className="audio-study-chapter"
                        role="button"
                        tabIndex={0}
                        onClick={() => onPlay(enrichedChapters, ch)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onPlay(enrichedChapters, ch);
                          }
                        }}
                      >
                        <div className="audio-study-chapter__info">
                          <div className="audio-study-chapter__title">
                            {ch.description
                              ? `${ch.name} - ${ch.description}`
                              : ch.name}
                          </div>
                        </div>

                        <div
                          className="audio-study-chapter__actions"
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                        >
                          <AudioBadge
                            progress={getChapterProgress(ch.id)}
                            hasPlaylist={!!ch.hasPlaylist}
                            hasAudioSummary={!!ch.hasAudioSummary}
                            onClick={() => onPlay(enrichedChapters, ch)}
                            onRestart={() => onRestart(ch.id)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </Collapse>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default CustomPlaylistSection;
